import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { clsx as cx } from 'clsx';
import Cookies from 'js-cookie';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down';
import Preferences from '../preferences';
import usePreferencesRoute from '~/modules/hooks/use-preferences-route';
import SwitchUser from '../switch-user';
import Button from '../button';
import Icon from '../icon';
import UserService from '../../../services/user-service';
import AuthService from '../../../services/auth-service';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { MenuLabel } from '../main-menu/main-menu.css';
import { useQueryClient } from '@tanstack/react-query';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem
} from '~/components/ui/dropdown-menu';

function AvatarMenu(props) {
	const {
		session,
		onLogout,
		onMenuItemClick,
		isMaximized = false
	} = props;

	const { signOut: signOutClerk } = useAuth();
	const [showMenu, setShowMenu] = useState(false);
	const [loggingOut, setLoggingOut] = useState(false);
	// Open state lives in the URL, so a refresh keeps the panel — and the tab — where it was.
	const preferencesRoute = usePreferencesRoute();
	const [showSwitchUser, setShowSwitchUser] = useState(false);

	const menuRef = useRef(null);

	const openMenu = (e) => {
		e.preventDefault();
		onMenuItemClick && onMenuItemClick();
		setShowMenu(true);
	};

	const closeMenu = (e) => {
		if( !menuRef?.current.contains(e.target) ) {
			setShowMenu(false);
		}
	};

	useEffect(() => {
		if( showMenu ) {
			setTimeout(() => {
				document.addEventListener('click', closeMenu);
			});
		}
		else {
			document.removeEventListener('click', closeMenu);
		}

		return () => {
			document.removeEventListener('click', closeMenu);
		};
	}, [showMenu]);

	const logOut = () => {
		signOutClerk();
		setLoggingOut(true);
	};

	useEffect(() => {
		if( loggingOut ) {
			onLogout();
		}
	}, [loggingOut]);

	const handlePreferencesClick = () => {
		setShowMenu(false);
		preferencesRoute.open();
	};

	useEffect(() => {
		const handler = () => {
			setShowMenu(false);
			preferencesRoute.open();
		};
		window.addEventListener('pixwel:open-preferences', handler);
		return () => window.removeEventListener('pixwel:open-preferences', handler);
	}, []);

	useEffect(() => {
		const handler = () => {
			setShowMenu(false);
			setShowSwitchUser(true);
		};
		window.addEventListener('pixwel:open-switch-user', handler);
		return () => window.removeEventListener('pixwel:open-switch-user', handler);
	}, []);

	useEffect(() => {
		const handler = () => {
			setShowMenu(false);
			queryClient.clear();
			AuthService.switchBack();
		};
		window.addEventListener('pixwel:switch-back', handler);
		return () => window.removeEventListener('pixwel:switch-back', handler);
	}, [queryClient]);

	const handleSwitchUserClick = () => {
		setShowMenu(false);
		setShowSwitchUser(true);
	};

	const queryClient = useQueryClient();
	const handleSwitchBackClick = () => {
		queryClient.clear();
		AuthService.switchBack();
	};

	const userName = session.name || session.emailNormal;
	const avatarSrc = `https://api.dicebear.com/7.x/initials/svg?seed=${userName}`;
	const isAdmin = UserService.isAdmin(session);
	const isImpersonating = AuthService.isImpersonating();
	// An impersonating session implies the original user was admin (API enforces it),
	// so the switch controls should remain available while impersonating.
	const canSwitch = isAdmin || isImpersonating;

	const recentUsersCookie = Cookies.get('pixwel.users.recent');
	const allRecentUsers = recentUsersCookie ? JSON.parse(recentUsersCookie) : [];
	const originalCookie = Cookies.get('pixwel.user.original');
	const originalUser = originalCookie ? JSON.parse(originalCookie) : null;

	const otherRecent = allRecentUsers
		.filter((u) => u.id !== session._id && u.id !== originalUser?.id)
		.sort((a, b) => b.count - a.count)
		.slice(0, 5);

	const recentUsers = (isImpersonating && originalUser)
		? [{ ...originalUser, $isOriginal: true }, ...otherRecent]
		: otherRecent;

	const handleRecentUserClick = async(u) => {
		if(u.$isOriginal) {
			queryClient.clear();
			await AuthService.switchBack();
			return;
		}
		if(!isImpersonating) {
			Cookies.set('pixwel.user.original', JSON.stringify({
				id: session._id,
				name: session.name,
				emailNormal: session.emailNormal
			}));
		}
		const updated = [...allRecentUsers];
		const i = updated.findIndex((x) => x.id === u.id);
		if( i > -1 ) {
			updated[i].count++;
		}
		else {
			updated.push({ ...u, count: 1 });
		}
		Cookies.set('pixwel.users.recent', JSON.stringify(updated));
		queryClient.clear();
		await AuthService.become(u.id);
	};

	return (
		<div className="relative" style={{ zIndex: 90 }} data-cy="avatar-menu-btn">
			{/* Avatar trigger — same nav-row pattern */}
			<button
				type="button"
				className={cx(
					'nav-row flex items-center flex-nowrap w-full',
					'border-0 border-l-2 border-solid border-transparent bg-transparent p-0',
					'text-sm font-bold text-white text-opacity-30 focus:outline-none cursor-pointer',
					'hover:bg-nav-hover hover:text-white transition-colors duration-fast'
				)}
				onClick={openMenu}
			>
				<img
					src={avatarSrc}
					className="rounded-full"
					style={{ margin: '0.625rem 1rem', width: '2rem', height: '2rem' }}
				/>
				<MenuLabel className={isMaximized ? 'block' : 'hidden'}>My Profile</MenuLabel>
			</button>

			{/* Flyout card */}
			<div
				ref={menuRef}
				className={cx(
					'absolute bottom-0 left-full rounded-xs p-4',
					'bg-midnight-100 shadow-card-hover',
					'transition-all duration-300 origin-bottom-left',
					showMenu ? 'scale-100 opacity-100 pointer-events-auto' : 'scale-0 opacity-0 pointer-events-none'
				)}
				style={{
					zIndex: 92,
					minWidth: '21rem',
					margin: showMenu ? '0.5rem' : '0',
					transitionProperty: 'transform, opacity, margin',
					transitionTimingFunction: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
					transitionDuration: '300ms'
				}}
			>
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<h6 className="m-0 font-bold truncate">{userName}</h6>
						{session.name && <p className="m-0 text-sm truncate">{session.emailNormal}</p>}
					</div>
					<button
						type="button"
						className="opacity-50 hover:opacity-100 transition-opacity duration-150 ease-out cursor-pointer border-0 bg-transparent p-0 focus:outline-none shrink-0"
						onClick={handlePreferencesClick}
					>
						<Icon use="settings" color="white" />
					</button>
				</div>

				<div className="flex flex-wrap items-center gap-2 mt-4">
					<UserButton />
					<Button
						onClick={logOut}
						disabled={loggingOut}
						loading={loggingOut}
						data-cy="logout-btn"
					>
						Log Out
					</Button>
					{ isImpersonating
						? (
							<DropdownMenu>
								<DropdownMenuTrigger
									type="button"
									data-testid="avatar-menu-switch-btn"
									className={cx(
										'inline-flex items-center gap-1',
										'py-1.5 px-4 rounded-xs',
										'border-solid border-2 border-transparent cursor-pointer whitespace-nowrap',
										'text-base font-medium leading-6',
										'bg-secondary text-secondary-foreground',
										'hover:bg-midnight-100',
										'focus:outline-none'
									)}
								>
									Switch
									<ChevronDown size={14} />
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start">
									<DropdownMenuItem
										onSelect={handleSwitchBackClick}
										data-testid="avatar-menu-switch-back-btn"
									>
										Switch Back
									</DropdownMenuItem>
									<DropdownMenuItem onSelect={handleSwitchUserClick}>
										Switch User
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						)
						: canSwitch && (
							<Button
								variant="secondary"
								lighten
								onClick={handleSwitchUserClick}
							>
								Switch User
							</Button>
						)}
				</div>

				{ canSwitch && recentUsers.length > 0 && (
					<div
						className="mt-4 pt-4 border-0 border-t border-solid"
						style={{ borderTopColor: 'rgba(255, 255, 255, 0.05)' }}
						data-testid="avatar-menu-recent-users"
					>
						<p className="m-0 mb-2 text-xs uppercase tracking-wider opacity-50">
							Recent Users
						</p>
						<ul className="m-0 p-0 list-none">
							{recentUsers.map((u) => {
								const seed = u.name || u.emailNormal;
								const src = `https://api.dicebear.com/7.x/initials/svg?seed=${seed}`;
								return (
									<li key={u.id} className="m-0">
										<button
											type="button"
											onClick={() => handleRecentUserClick(u)}
											className={cx(
												'recent-user-btn w-full flex items-center gap-2 py-2 px-2 rounded-xs',
												'bg-transparent border-0 cursor-pointer text-left text-white',
												'opacity-60 hover:opacity-100',
												'transition-opacity duration-150 focus:outline-none'
											)}
										>
											<img
												src={src}
												className="rounded-full shrink-0"
												style={{ width: '1.5rem', height: '1.5rem' }}
											/>
											<div className="flex flex-col min-w-0">
												<span className="text-sm font-medium truncate">{u.name}</span>
												<span className="text-xs opacity-75 truncate">{u.emailNormal}</span>
											</div>
										</button>
									</li>
								);
							})}
						</ul>
						<style>{`
							.recent-user-btn:hover {
								background-color: rgba(255, 255, 255, 0.04);
							}
						`}</style>
					</div>
				) }
			</div>

			<Preferences
				show={preferencesRoute.isOpen}
				tab={preferencesRoute.tab}
				onTabSelected={preferencesRoute.selectTab}
				onClose={preferencesRoute.close}
			/>

			<SwitchUser
				show={showSwitchUser}
				onClose={() => setShowSwitchUser(false)}
				current={session}
			/>

		</div>
	);
}

AvatarMenu.propTypes = {
	session: PropTypes.object.isRequired,
	onLogout: PropTypes.func.isRequired,
	onMenuItemClick: PropTypes.func,
	isMaximized: PropTypes.bool
};

export default AvatarMenu;
